<template>
    <div class="create-post" v-if="currentUser">
        <div class="page-header">
            <div class="page-header-left">
                <router-link to="/admin-profile/blog" class="back-link">&#8592; Powrót do bloga</router-link>
                <h1>Nowy wpis blogowy</h1>
            </div>
        </div>

        <div class="form-layout">
            <div class="form-main section-card">
                <h2>Treść wpisu</h2>

                <div class="form-group">
                    <label for="newEntryTitle">
                      Tytuł <span class="required">*</span>
                    </label>
                    <input
                        type="text"
                        id="newEntryTitle"
                        v-model="form.title"
                        placeholder="Wpisz tytuł wpisu..."
                        required
                    >
                </div>

                <div class="form-group">
                    <label for="newEntryShortDescription">
                      Krótki opis
                      <span class="required">*</span>
                    </label>
                    <textarea
                        id="newEntryShortDescription"
                        v-model="form.shortDescription"
                        placeholder="Wpisz krótki opis wpisu (widoczny na liście)..."
                        rows="3"
                        required
                    ></textarea>
                </div>

                <div class="form-group">
                    <label>Treść <span class="required">*</span></label>
                    <CKEditor v-model="form.content"></CKEditor>
                </div>
            </div>

            <div class="form-sidebar">
                <div class="section-card">
                    <h2>Szczegóły wpisu</h2>

                    <div class="form-group">
                        <label for="newEntryAuthor">
                          Autor
                          <span class="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="newEntryAuthor"
                            v-model="form.author"
                            placeholder="Imię i nazwisko autora..."
                            required
                        >
                    </div>

                    <div class="form-group">
                        <label for="newEntryTarget">
                          Odbiorcy
                          <span class="required">*</span>
                        </label>
                        <select id="newEntryTarget" v-model="form.target" required>
                            <option>do wszystkich</option>
                            <option>do specjalistów</option>
                            <option>do właścicieli zwierząt</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label for="newEntryCategory">
                          Kategoria
                          <span class="required">*</span>
                        </label>
                        <select id="newEntryCategory" v-model="form.categoryId" required>
                            <option value="" disabled>Wybierz kategorię...</option>
                            <option :value="cat.id" v-for="cat in categoriesList" :key="cat.id">{{ cat.title }}</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label for="newEntryPhoto">
                          Zdjęcie
                          <span class="required">*</span>
                        </label>
                        <input
                            type="file"
                            accept="image/*"
                            id="newEntryPhoto"
                            ref="newEntryPhoto"
                            @change="selectPhoto"
                            required
                        >
                        <div v-if="photoPreview" class="photo-preview">
                            <img :src="photoPreview" alt="Podgląd zdjęcia">
                        </div>
                    </div>

                    <div class="form-actions">
                        <button @click="submitForm" class="btn-primary" :disabled="isSubmitting">
                            {{ isSubmitting ? 'Dodawanie...' : 'Opublikuj wpis' }}
                        </button>
                        <router-link to="/admin-profile/blog" class="btn-outline">Anuluj</router-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import CKEditor from '@/components/CKEditor.vue';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';

const BLOG_POST_DRAFT_KEY = 'create-blog-post-draft-v1';

export default {
  name: 'CreateBlogPost',
  components: { CKEditor },
  setup() {
    return {
      authStore: useAuthStore(),
      appStore: useAppStore(),
    };
  },
  data() {
    return {
      form: {
        title: '',
        shortDescription: '',
        content: '',
        author: '',
        target: 'do wszystkich',
        categoryId: '',
      },
      photo: null,
      photoPreview: null,
      categoriesList: [],
      isSubmitting: false,
    };
  },
  computed: {
    currentUser() {
      return this.authStore.user;
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.saveDraft();
      },
    },
  },
  mounted() {
    this.restoreDraft();
    window.addEventListener('app:session-expired', this.handleSessionExpired);

    if (!this.currentUser) {
      this.$router.push('/login');
    } else if (this.currentUser.role_id !== 1) {
      this.$router.push('/login');
    }
    this.getCategories();
  },
  beforeUnmount() {
    window.removeEventListener('app:session-expired', this.handleSessionExpired);
  },
  methods: {
    getCategories() {
      axios.post('/BlogCategorySearch', { page: 0, limit: 50 })
          .then(res => {
            this.categoriesList = res.data.items;
          });
    },
    saveDraft() {
      const draft = {
        form: this.form,
      };
      localStorage.setItem(BLOG_POST_DRAFT_KEY, JSON.stringify(draft));
    },
    restoreDraft() {
      const raw = localStorage.getItem(BLOG_POST_DRAFT_KEY);
      if (!raw) return;
      try {
        const draft = JSON.parse(raw);
        if (draft && draft.form) {
          this.form = {
            ...this.form,
            ...draft.form,
          };
        }
      } catch (e) {
        localStorage.removeItem(BLOG_POST_DRAFT_KEY);
      }
    },
    clearDraft() {
      localStorage.removeItem(BLOG_POST_DRAFT_KEY);
    },
    handleSessionExpired() {
      this.saveDraft();
    },
    selectPhoto() {
      const file = this.$refs.newEntryPhoto.files.item(0);
      if (file) {
        this.photo = file;
        this.photoPreview = URL.createObjectURL(file);
      }
    },
    submitForm() {
      if (!this.form.title || !this.form.shortDescription || !this.form.content
          || !this.form.author || !this.form.categoryId || !this.photo) {
        this.appStore.setIsAlert('Uzupełnij wszystkie pola i wybierz zdjęcie');
        return;
      }

      this.isSubmitting = true;

      const formData = new FormData();
      formData.append('title', this.form.title);
      formData.append('short_description', this.form.shortDescription);
      formData.append('description', this.form.content);
      formData.append('author', this.form.author);
      formData.append('target', this.form.target);
      formData.append('blog_category_id', String(Number(this.form.categoryId)));
      formData.append('photo', this.photo);

      axios.post('/BlogPost', formData)
          .then(res => {
            if (String(res.status).charAt(0) === '2') {
              this.clearDraft();
              this.appStore.setIsSuccess('Pomyślnie dodano wpis');
              this.$router.push('/admin-profile/blog');
            }
          })
          .catch((error) => {
            if (error?.response?.status === 401) {
              this.appStore.setIsAlert('Sesja wygasła. Zaloguj się ponownie — szkic został zapisany.');
              return;
            }
            this.appStore.setIsAlert('Wystąpił błąd podczas dodawania wpisu');
          })
          .finally(() => {
            this.isSubmitting = false;
          });
    },
  },
};
</script>

<style scoped>
.create-post {
    max-width: 1200px;
    margin: 0 auto;
    padding: 32px 16px;
    font-family: sans-serif;
    box-sizing: border-box;
}

.page-header {
    margin-bottom: 32px;
}

.back-link {
    display: inline-block;
    font-size: 1.4rem;
    color: #10b981;
    text-decoration: none;
    font-weight: 600;
    margin-bottom: 8px;
    transition: color 0.2s;
}

.back-link:hover {
    color: #059669;
}

.page-header h1 {
    font-size: 2.8rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
}

.form-layout {
    display: flex;
    gap: 24px;
    align-items: flex-start;
}

.form-main {
    flex: 1 1 0;
    min-width: 0;
}

.form-sidebar {
    width: 320px;
    flex-shrink: 0;
}

@media (max-width: 900px) {
    .form-layout {
        flex-direction: column;
    }
    .form-sidebar {
        width: 100%;
    }
}

.section-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 6px rgba(0,0,0,0.08);
    padding: 24px;
    box-sizing: border-box;
    width: 100%;
}

.section-card h2 {
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 20px 0;
    padding-bottom: 12px;
    border-bottom: 1px solid #e5e7eb;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 20px;
}

.form-group label {
    font-size: 1.5rem;
    font-weight: 600;
    color: #374151;
}

.form-group input[type="text"],
.form-group input[type="file"],
.form-group textarea,
.form-group select {
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 1.5rem;
    outline: none;
    transition: border-color 0.2s;
    resize: vertical;
    box-sizing: border-box;
    width: 100%;
}

.form-group input[type="file"] {
    padding: 8px;
    cursor: pointer;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
    border-color: #10b981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
}

.photo-preview {
    margin-top: 10px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
}

.photo-preview img {
    width: 100%;
    max-height: 200px;
    object-fit: cover;
    display: block;
}

.form-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 8px;
}

.btn-primary {
    background-color: #f97316;
    color: #fff;
    padding: 12px 20px;
    border-radius: 8px;
    border: none;
    font-size: 1.5rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    display: block;
    text-align: center;
    transition: background-color 0.2s;
}

.btn-primary:hover:not(:disabled) {
    background-color: #ea6c0a;
}

.btn-primary:disabled {
    background-color: #fdba74;
    cursor: not-allowed;
}

.btn-outline {
    background-color: transparent;
    color: #6b7280;
    padding: 12px 20px;
    border-radius: 8px;
    border: 1px solid #d1d5db;
    font-size: 1.5rem;
    cursor: pointer;
    text-decoration: none;
    display: block;
    text-align: center;
    transition: border-color 0.2s, color 0.2s;
}

.btn-outline:hover {
    border-color: #9ca3af;
    color: #374151;
}

.required {
  color: red;
}

@media (max-width: 480px) {
    .create-post {
        padding: 16px 12px;
    }
    .page-header {
        margin-bottom: 20px;
    }
    .page-header h1 {
        font-size: 2.2rem;
    }
    .section-card {
        padding: 16px 12px;
        border-radius: 8px;
    }
    .section-card h2 {
        font-size: 1.8rem;
    }
    .form-layout {
        gap: 16px;
    }
}
</style>
